import { useEffect } from 'react';
import { type ChannelFilters } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';
import { isChannelInCase } from '@features/components/Chat/utils';
import { useChatPanelStore } from '@features/store/chat-panel.store';

type Props = {
  caseId?: string;
  filterVersion: number;
  streamChatFilters?: ChannelFilters;
};

export const useRestoreActiveChannel = ({ caseId, filterVersion, streamChatFilters }: Props) => {
  const { setActiveChannel, client } = useChatContext();
  const { caseActiveChannels, allChatsActiveChannel, clearActiveChannel } = useChatPanelStore();

  useEffect(() => {
    if (!client || !streamChatFilters) return undefined;

    const saved = caseId ? caseActiveChannels[caseId] : allChatsActiveChannel;
    if (!saved) return undefined;

    // watch() may resolve after the user switched to another case; drop the stale result
    let cancelled = false;
    const channel = client.channel(saved.type, saved.id);
    channel
      .watch()
      .then(() => {
        if (cancelled || !isChannelInCase(channel, caseId)) return;
        setActiveChannel(channel);
      })
      .catch(() => {
        if (cancelled) return;
        clearActiveChannel(caseId);
      });

    return () => {
      cancelled = true;
    };
    // Only run once per context (caseId / filter change handled by the reset effect)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, filterVersion, client, streamChatFilters]);
};
